import {Fragment, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useSelector} from "react-redux";
import moment from "moment";

import AddIcon from "@mui/icons-material/Add";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import ChoiceChips from "@/components/ChoiceChips";
import Loading from "@/components/Loading";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import MainActions from "@/components/MainActions";
import EventsCalendar from "@/components/Calendar/Events";

import {useCategories} from "@/utils/hooks";
import {CalendarEventsAPI} from "@/api/calendarEvents";
import {getRosterName, visibleRosters} from "@/utils/functions";
import {
	useScoutingPipeline,
	monitoredInMatch,
	monitoredRosterCounts,
	monitoredWithoutRoster,
} from "@/utils/scoutingPipeline";

import {ERROR_OPERATION, getInternalClubId} from "@/utils/constants";

import "./index.scss";

function calendarTeamName(clubName, rosterName) {
	const club = (clubName || "").trim().replace(/\s+/g, " ");
	const roster = (rosterName || "").trim().replace(/\s+/g, " ");
	if (!club) return roster;
	if (!roster) return club;
	const normalizedClub = club.toLocaleLowerCase("it");
	const normalizedRoster = roster.toLocaleLowerCase("it");
	if (normalizedRoster === normalizedClub) return club;
	if (
		normalizedRoster.startsWith(normalizedClub) &&
		/^[\s—–-]/.test(roster.slice(club.length))
	) {
		const suffix = roster.slice(club.length).replace(/^[\s—–-]+/, "");
		return suffix ? club + " — " + suffix : club;
	}
	return club + " — " + roster;
}

const MY_ROSTERS = {value: "my-rosters", label: "Le mie rose"};
const ALL_ROSTERS = {value: null, label: "Tutte le rose"};
const ALL_COMPETITIONS = {value: null, label: "Tutte le competizioni"};
const ALL_ROUNDS = {value: null, label: "Tutte le giornate"};
const PLANNING_OPTIONS = [
	{value: "planned", label: "Da osservare"},
	{value: "monitored", label: "Da vedere"},
	{value: "all", label: "Tutte le partite"},
];
const EVENT_TYPES = [
	{value: "all", label: "Tutto"},
	{value: "training", label: "Solo allenamenti"},
	{value: "match", label: "Solo partite"},
];
const AGENDA_STEP = 50;
const agendaDay = (event) => moment(event.startDate).format("YYYY-MM-DD");

// Agenda della stagione: parte dalla prima gara da oggi in poi e si allunga a richiesta,
// 50 eventi alla volta, con un'intestazione per giorno.
function SeasonAgenda({title, events, onOpen}) {
	const firstUpcoming = useMemo(() => {
		const today = moment().startOf("day");
		const index = events.findIndex((e) => !moment(e.startDate).isBefore(today));
		return index < 0 ? Math.max(0, events.length - AGENDA_STEP) : index;
	}, [events]);
	const dayCounts = useMemo(() => {
		const counts = {};
		events.forEach((e) => {
			counts[agendaDay(e)] = (counts[agendaDay(e)] || 0) + 1;
		});
		return counts;
	}, [events]);
	// Le aggiunte valgono solo per l'elenco su cui sono state fatte: un filtro nuovo riparte da oggi.
	const [expanded, setExpanded] = useState({events: null, before: 0, after: 0});
	const extra =
		expanded.events === events ? expanded : {events, before: 0, after: 0};
	const start = Math.max(0, firstUpcoming - extra.before);
	const end = firstUpcoming + AGENDA_STEP + extra.after;
	const visible = events.slice(start, end);
	const onlyMatches = events.every((e) => e.__eventType === "match");
	const noun = (n) =>
		onlyMatches
			? n === 1
				? "partita"
				: "partite"
			: n === 1
				? "evento"
				: "eventi";
	const today = moment().format("YYYY-MM-DD");

	return (
		<div className="ecp-agenda">
			<div className="ecp-agenda__head">
				<h3>{title}</h3>
				{events.length > 0 && (
					<span>
						{events.length.toLocaleString("it-IT")} {noun(events.length)}
					</span>
				)}
			</div>
			{!events.length && <p>Nessun evento per i filtri selezionati.</p>}
			{start > 0 && (
				<button
					className="ecp-agenda__more"
					onClick={() =>
						setExpanded({...extra, before: extra.before + AGENDA_STEP})
					}
				>
					Mostra le precedenti ({start.toLocaleString("it-IT")})
				</button>
			)}
			{visible.map((event, index) => {
				const day = agendaDay(event);
				const newDay = index === 0 || agendaDay(visible[index - 1]) !== day;
				return (
					<Fragment key={event.__eventType + event.id}>
						{newDay && (
							<div
								className={
									"ecp-agenda__day" + (day === today ? " is-today" : "")
								}
							>
								<span>
									{moment(event.startDate).locale("it").format("dddd D MMMM")}
								</span>
								{day === today && <em>Oggi</em>}
								<small>
									{dayCounts[day]} {noun(dayCounts[day])}
								</small>
							</div>
						)}
						<button className="ecp-agenda__row" onClick={() => onOpen(event)}>
							<span>
								{event.startTimeToBeConfirmed
									? "OdD"
									: moment(event.startDate).format("HH:mm")}
							</span>
							<span>
								<strong>
									{event.__eventType === "match"
										? [event.homeTeamName, event.awayTeamName].join(" Vs ")
										: event.rosterName || event.description || "Seduta"}
								</strong>
								<small>
									{[
										event.competitionName || event.fieldName,
										event.monitoredCount
											? event.monitoredCount +
												(event.monitoredCount === 1
													? " monitorato"
													: " monitorati")
											: null,
									]
										.filter(Boolean)
										.join(" · ")}
								</small>
							</span>
							<span>
								{event.__eventType === "match" &&
								Number.isInteger(event.homeClubGoals) &&
								Number.isInteger(event.awayClubGoals)
									? `${event.homeClubGoals}–${event.awayClubGoals}`
									: ""}
							</span>
						</button>
					</Fragment>
				);
			})}
			{end < events.length && (
				<button
					className="ecp-agenda__more"
					onClick={() =>
						setExpanded({...extra, after: extra.after + AGENDA_STEP})
					}
				>
					Mostra le successive ({(events.length - end).toLocaleString("it-IT")})
				</button>
			)}
		</div>
	);
}

const storedFilter = (key, fallback) => {
	try {
		return JSON.parse(sessionStorage.getItem(key)) || fallback;
	} catch {
		return fallback;
	}
};
function ActivityCalendarView(props) {
	const {handleNotification, mode, storagePrefix} = props;
	const userRole = useSelector((state) => state?.user?.roleName);
	const canCreate = !["Player", "Analyst"].includes(userRole);
	const planning = mode === "scouting";
	const compactFilters = planning || mode === "all";
	const initialType =
		mode === "all"
			? EVENT_TYPES[0]
			: mode === "training"
				? EVENT_TYPES[1]
				: EVENT_TYPES[2];
	const initialRoster = planning ? ALL_ROSTERS : MY_ROSTERS;
	const readFilter = (key, fallback) =>
		storedFilter(storagePrefix + key, fallback);
	const {categories} = useCategories({setLoading: false, formatted: true});
	const [planningFilter, setPlanningFilter] = useState(() =>
		readFilter("_planning", {value: "planned", label: "Da osservare"}),
	);
	const [scoutSearch, setScoutSearch] = useState(() =>
		readFilter("_scout", ""),
	);
	const [categoryFilter, setCategoryFilter] = useState(() =>
		readFilter("_category", null),
	);
	const [roundFilter, setRoundFilter] = useState(() =>
		readFilter("_round", ALL_ROUNDS),
	);
	const {pipeline, error: pipelineError} = useScoutingPipeline({
		enabled: planning,
	});
	const showMonitored = planning && planningFilter.value === "monitored";

	const navigate = useNavigate();
	const location = useLocation();
	const season = useSelector((state) => state?.season);
	const seasonId = season?.id;

	const [loading, setLoading] = useState(false);
	const [matches, setMatches] = useState([]);
	const [trainings, setTrainings] = useState([]);
	const [rosters, setRosters] = useState([]);
	const [rosterFilter, setRosterFilter] = useState(() =>
		readFilter("_roster", initialRoster),
	);
	const [eventTypeFilter, setEventTypeFilter] = useState(() => {
		const stored = readFilter("_type", initialType);
		return mode === "all"
			? EVENT_TYPES.find((t) => t.value === stored.value) || initialType
			: initialType;
	});
	const [competitionFilter, setCompetitionFilter] = useState(() =>
		readFilter("_competition", ALL_COMPETITIONS),
	);
	const [competitions, setCompetitions] = useState([]);
	const userId = useSelector((state) => state?.user?.userId);
	const [currentView, setCurrentView] = useState(() =>
		readFilter("_view", "month"),
	);
	const [currentDate, setCurrentDate] = useState(
		() => new Date(readFilter("_date", new Date().toISOString())),
	);

	// mini create menu
	const [createMenu, setCreateMenu] = useState(null); // { x, y, date }
	const menuRef = useRef(null);

	// Persist filter
	useEffect(() => {
		sessionStorage.setItem(
			storagePrefix + "_roster",
			JSON.stringify(rosterFilter),
		);
	}, [rosterFilter, storagePrefix]);

	// Close menu on outside click
	useEffect(() => {
		if (!createMenu) return;
		const handler = (e) => {
			if (menuRef.current && !menuRef.current.contains(e.target)) {
				setCreateMenu(null);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [createMenu]);

	useEffect(() => {
		sessionStorage.setItem(
			storagePrefix + "_type",
			JSON.stringify(eventTypeFilter),
		);
		sessionStorage.setItem(
			storagePrefix + "_competition",
			JSON.stringify(competitionFilter),
		);
	}, [eventTypeFilter, competitionFilter, storagePrefix]);
	useEffect(() => {
		for (const [key, value] of Object.entries({
			_view: currentView,
			_date: currentDate.toISOString(),
			_planning: planningFilter,
			_scout: scoutSearch,
			_category: categoryFilter,
			_round: roundFilter,
		}))
			sessionStorage.setItem(storagePrefix + key, JSON.stringify(value));
	}, [
		currentView,
		currentDate,
		planningFilter,
		scoutSearch,
		categoryFilter,
		roundFilter,
		storagePrefix,
	]);

	useEffect(() => {
		let active = true;
		setMatches([]);
		setTrainings([]);
		setRosters([]);
		setCompetitions([]);
		if (!seasonId) {
			setLoading(false);
			return;
		}
		const ref = moment(currentDate);
		const fromDate = (
			currentView === "season"
				? moment(season?.startDate)
				: currentView === "week"
					? ref.clone().startOf("isoWeek")
					: ref.clone().startOf("month").startOf("isoWeek")
		).format("YYYY-MM-DD");
		const toDate = (
			currentView === "season"
				? moment(season?.endDate)
				: currentView === "week"
					? ref.clone().endOf("isoWeek")
					: ref.clone().endOf("month").endOf("isoWeek")
		).format("YYYY-MM-DD");
		setLoading(true);
		CalendarEventsAPI.list({
			seasonId,
			fromDate,
			toDate,
			rosterId:
				rosterFilter?.value === MY_ROSTERS.value
					? undefined
					: rosterFilter?.value || undefined,
			competitionSeasonId: competitionFilter?.value || undefined,
			eventType: eventTypeFilter.value,
			scoutingPlanned:
				planning && planningFilter.value === "planned" ? true : undefined,
			scoutSearch: planning ? scoutSearch || undefined : undefined,
			categoryId: categoryFilter?.value || undefined,
		})
			.then((response) => {
				if (!active) return;
				const ownRosterIds = new Set(
					(response.rosters || [])
						.filter((r) => r.clubId === getInternalClubId())
						.map((r) => r.id),
				);
				const ownOnly = rosterFilter?.value === MY_ROSTERS.value;
				const visibleMatches = (response.matches || []).filter(
					(m) =>
						!ownOnly ||
						ownRosterIds.has(m.homeRosterId) ||
						ownRosterIds.has(m.awayRosterId),
				);
				const visibleTrainings = (response.trainings || []).filter(
					(t) => !ownOnly || ownRosterIds.has(t.rosterId),
				);
				const ownCompetitionIds = new Set(
					visibleMatches.map((m) => m.competitionSeasonId),
				);
				const visibleCompetitions = (response.competitions || []).filter(
					(e) =>
						!ownOnly ||
						ownCompetitionIds.has(e.id) ||
						(e.participants || []).some((p) => ownRosterIds.has(p.rosterId)) ||
						(e.groups || []).some((g) =>
							(g.rosters || []).some((r) => ownRosterIds.has(r.rosterId)),
						),
				);
				const rosterOptions = [
					MY_ROSTERS,
					ALL_ROSTERS,
					...visibleRosters(response.rosters || []).map((r) => ({
						value: r.id,
						label: getRosterName(r),
					})),
				];
				// Il backend non restituisce tutte le competizioni delle partite:
				// si completano con quelle delle partite caricate.
				const listedIds = new Set(visibleCompetitions.map((e) => e.id));
				const fromMatches = [
					...new Map(
						visibleMatches
							.filter(
								(m) =>
									m.competitionSeasonId &&
									!listedIds.has(m.competitionSeasonId),
							)
							.map((m) => [m.competitionSeasonId, m.competitionName]),
					),
				];
				const competitionOptions = [
					ALL_COMPETITIONS,
					...visibleCompetitions.map((e) => ({
						value: e.id,
						label: e.competition?.name || "Competizione",
					})),
					...fromMatches.map(([value, label]) => ({
						value,
						label: label || "Competizione",
					})),
				];
				setRosters(rosterOptions);
				setCompetitions(competitionOptions);
				setRosterFilter(
					(prev) =>
						rosterOptions.find((r) => r.value === prev?.value) || initialRoster,
				);
				setCompetitionFilter(
					(prev) =>
						competitionOptions.find((c) => c.value === prev?.value) ||
						ALL_COMPETITIONS,
				);
				setMatches(visibleMatches.map((m) => ({...m, __eventType: "match"})));
				setTrainings(
					visibleTrainings.map((t) => ({
						...t,
						__eventType: "training",
					})),
				);
			})
			.catch(() => {
				if (active) handleNotification(ERROR_OPERATION, "error");
			})
			.finally(() => {
				if (active) setLoading(false);
			});
		return () => {
			active = false;
		};
	}, [
		currentDate,
		currentView,
		seasonId,
		userId,
		rosterFilter?.value,
		competitionFilter?.value,
		eventTypeFilter.value,
		planningFilter.value,
		scoutSearch,
		categoryFilter?.value,
	]);

	// Client-side filter by roster
	const rosterLabelMap = useMemo(() => {
		const map = {};
		rosters.forEach((r) => {
			if (r.value) map[r.value] = r.label;
		});
		return map;
	}, [rosters]);

	const monitoredCounts = useMemo(
		() => monitoredRosterCounts(pipeline, seasonId),
		[pipeline, seasonId],
	);

	const roundOptions = useMemo(() => {
		const rounds = [
			...new Set(
				matches
					.filter(
						(m) =>
							!competitionFilter?.value ||
							m.competitionSeasonId === competitionFilter.value,
					)
					.map((m) => m.round)
					.filter(Boolean),
			),
		].sort((a, b) => a.localeCompare(b, "it", {numeric: true}));
		return [
			ALL_ROUNDS,
			...rounds.map((round) => ({value: round, label: "Giornata " + round})),
		];
	}, [matches, competitionFilter?.value]);

	const data = useMemo(
		() =>
			[
				...matches
					.map((m) => ({
						...m,
						monitoredCount: planning
							? m.monitoredCount ?? monitoredInMatch(m, monitoredCounts)
							: 0,
						scoutNames: [
							(m.assignedScouts || []).map((s) => s.name).join(", "),
							m.scoutNames,
						]
							.filter(Boolean)
							.join(" · "),
					}))
					.filter(
						(m) =>
							(!showMonitored || m.monitoredCount > 0) &&
							(!planning ||
								!roundFilter?.value ||
								m.round === roundFilter.value),
					)
					.map((m) => {
						const homeOwn =
							m.homeClubId === getInternalClubId() &&
							!!rosterLabelMap[m.homeRosterId];
						const awayOwn =
							m.awayClubId === getInternalClubId() &&
							!!rosterLabelMap[m.awayRosterId];
						let ownSide =
							homeOwn !== awayOwn ? (homeOwn ? "home" : "away") : null;
						if (homeOwn && awayOwn) {
							if (rosterFilter?.value === m.homeRosterId) ownSide = "home";
							else if (rosterFilter?.value === m.awayRosterId) ownSide = "away";
						}
						return {
							...m,
							showScouting: planning,
							ownSide,
							homeTeamName: calendarTeamName(
								m.homeClub,
								rosterLabelMap[m.homeRosterId],
							),
							awayTeamName: calendarTeamName(
								m.awayClub,
								rosterLabelMap[m.awayRosterId],
							),
						};
					}),
				...trainings,
			].sort((a, b) => new Date(a.startDate) - new Date(b.startDate)),
		[
			matches,
			trainings,
			rosterLabelMap,
			rosterFilter?.value,
			planning,
			monitoredCounts,
			showMonitored,
			roundFilter?.value,
		],
	);

	const matchCount = data.filter((e) => e.__eventType === "match").length;
	const trainingCount = data.filter((e) => e.__eventType === "training").length;

	// ── Slot click: show mini menu ───────────────────────────────────────────
	const handleSelectSlot = (slot) => {
		// Calculate position relative to viewport
		const nativeEvent = slot.box || slot.bounds;
		if (!nativeEvent) return;
		const x = nativeEvent.x ?? nativeEvent.left;
		const y = nativeEvent.y ?? nativeEvent.top;
		setCreateMenu({x, y, date: slot.start});
	};

	const openEvent = (event) =>
		navigate(
			(event.__eventType === "match" ? "/matches/" : "/trainings/") + event.id,
			{
				state: {
					returnTo: location.pathname,
					initialTab: planning ? "scouting" : "main_info",
				},
			},
		);
	const goCreate = (type) => {
		const startDate = createMenu?.date
			? moment(createMenu.date).toISOString()
			: null;
		setCreateMenu(null);
		if (type === "training") {
			navigate("/trainings/new", {state: {startDate}});
		} else {
			navigate(planning ? "/scouting/planning/new" : "/matches/new", {
				state: {startDate, returnTo: location.pathname},
			});
		}
	};

	// Due creazioni con la stessa icona: qui il pulsante tiene anche il testo
	const createActions = !canCreate
		? []
		: mode === "all"
			? [
					{
						icon: <AddIcon />,
						label: "Allenamento",
						title: "Nuovo allenamento",
						onClick: () =>
							navigate("/trainings/new", {
								state: {returnTo: location.pathname},
							}),
					},
					{
						icon: <AddIcon />,
						label: "Partita",
						title: "Nuova partita",
						onClick: () =>
							navigate("/matches/new", {
								state: {returnTo: location.pathname},
							}),
					},
				]
			: mode === "training"
				? [
						{
							icon: <AddIcon />,
							title: "Nuovo allenamento",
							onClick: () => navigate("/trainings/new"),
						},
					]
				: [];

	return (
		<Page>
			{showMonitored &&
				!pipelineError &&
				monitoredWithoutRoster(pipeline, seasonId).length > 0 && (
					<div role="status">
						{monitoredWithoutRoster(pipeline, seasonId).length} monitorati senza
						squadra univoca nella stagione: esclusi dal filtro gare.
					</div>
				)}
			{showMonitored && pipelineError && (
				<div role="alert">
					Monitorati non disponibili o accesso non consentito. Riprova
					aggiornando la pagina.
				</div>
			)}
			<Loading visible={loading} />
			<Breadcrumbs
				current={
					planning
						? "Planning scouting"
						: mode === "training"
							? "Allenamenti"
							: "Calendario"
				}
				actions={
					!!createActions.length && <MainActions actions={createActions} />
				}
			/>
			<div
				className={`ecp${compactFilters ? " ecp--compact" : ""}${planning ? " ecp--planning" : mode === "all" ? " ecp--general" : ""}`}
			>
				{/* ── Toolbar ─────────────────────────────────────────── */}
				<div className="ecp__toolbar">
					<div className="ecp__toolbar-left">
						<ChoiceChips
							options={[
								{value: "week", label: "Settimana"},
								{value: "month", label: "Mese"},
								{value: "season", label: "Elenco"},
							]}
							value={currentView}
							onChange={setCurrentView}
						/>
						{!planning && (
							<div className="ecp__legend">
								<span className="ecp__legend-item">
									<span className="ecp__legend-dot ecp__legend-dot--match" />
									Partite
									{matchCount > 0 && (
										<span className="ecp__legend-count">{matchCount}</span>
									)}
								</span>
								<span className="ecp__legend-item">
									<span className="ecp__legend-dot ecp__legend-dot--training" />
									Allenamenti
									{trainingCount > 0 && (
										<span className="ecp__legend-count">{trainingCount}</span>
									)}
								</span>
							</div>
						)}
						{canCreate && currentView !== "season" && (
							<span className="ecp__hint">
								Clicca su un giorno per aggiungere
							</span>
						)}
					</div>

					<div className="ecp__toolbar-right">
						{planning && (
							<>
								<div className="ecp__filter">
									<VLFSelectNew
										label="Scouting"
										sorted={false}
										isClearable={false}
										noMargin
										value={planningFilter}
										onChange={setPlanningFilter}
										options={PLANNING_OPTIONS}
									/>
								</div>
								<div className="ecp__filter">
									<VLFTextField
										label="Osservatore"
										placeholder="Cerca osservatore"
										size="small"
										value={scoutSearch}
										onChange={(e) => setScoutSearch(e.target.value)}
										inputProps={{"aria-label": "Osservatore"}}
									/>
								</div>
							</>
						)}
						<div className="ecp__filter">
							<VLFSelectNew
								label="Categoria"
								options={categories}
								value={categoryFilter}
								onChange={setCategoryFilter}
								noMargin
							/>
						</div>
						{!planning && (
							<div className="ecp__filter">
								<VLFSelectNew
									label="Eventi"
									isDisabled={mode !== "all"}
									options={mode === "all" ? EVENT_TYPES : [initialType]}
									sorted={false}
									isClearable={false}
									value={eventTypeFilter}
									onChange={(value) => {
										setEventTypeFilter(value);
										if (value.value !== "match")
											setCompetitionFilter(ALL_COMPETITIONS);
									}}
									noMargin
								/>
							</div>
						)}
						<div className="ecp__filter">
							<VLFSelectNew
								label="Rosa"
								options={rosters}
								sorted={false}
								isClearable={false}
								value={rosterFilter}
								onChange={setRosterFilter}
								noMargin
							/>
						</div>
						<div className="ecp__filter">
							<VLFSelectNew
								label="Competizione"
								options={competitions}
								sorted={false}
								isClearable={false}
								value={competitionFilter}
								isDisabled={eventTypeFilter.value === "training"}
								onChange={(value) => {
									setCompetitionFilter(value);
									setRoundFilter(ALL_ROUNDS);
									if (value.value) setEventTypeFilter(EVENT_TYPES[2]);
								}}
								noMargin
							/>
						</div>
						{planning && (
							<div className="ecp__filter">
								<VLFSelectNew
									label="Giornata"
									options={roundOptions}
									sorted={false}
									isClearable={false}
									value={
										roundOptions.find((r) => r.value === roundFilter?.value) ||
										ALL_ROUNDS
									}
									onChange={setRoundFilter}
									noMargin
								/>
							</div>
						)}
					</div>
				</div>

				{showMonitored && !monitoredCounts.size && (
					<div className="ecp__notice">
						<span>
							Nessun monitorato con squadra in questa stagione. Aggiungili con
							la stellina dalla scheda giocatore o dalle rose della gara.
						</span>
						<MainActions
							actions={[
								{
									label: "Vai al database",
									title: "Vai al database",
									onClick: () => navigate("/scouting/players"),
								},
							]}
						/>
					</div>
				)}

				{/* ── Calendar ─────────────────────────────────────────── */}
				<div className="ecp__calendar">
					{currentView === "season" ? (
						<SeasonAgenda
							title={season?.name}
							events={data}
							onOpen={openEvent}
						/>
					) : (
						<EventsCalendar
							events={data}
							view={currentView === "week" ? "week" : "month"}
							onSelectEvent={openEvent}
							onSelectSlot={canCreate ? handleSelectSlot : undefined}
							onNavigate={(d) => setCurrentDate(d)}
							onView={(v) => setCurrentView(v)}
							date={currentDate}
						/>
					)}
				</div>
			</div>

			{/* ── Create mini menu ────────────────────────────────────── */}
			{createMenu && (
				<div
					ref={menuRef}
					className="ecp-create-menu"
					style={{
						top: Math.min(createMenu.y, window.innerHeight - 120),
						left: Math.min(createMenu.x, window.innerWidth - 200),
					}}
				>
					<div className="ecp-create-menu__date">
						{moment(createMenu.date).format("ddd D MMM")}
					</div>
					<button
						hidden={planning}
						className="ecp-create-menu__item ecp-create-menu__item--training"
						onClick={() => goCreate("training")}
					>
						<span className="ecp-create-menu__dot" />
						Nuovo allenamento
					</button>
					<button
						hidden={mode === "training"}
						className="ecp-create-menu__item ecp-create-menu__item--match"
						onClick={() => goCreate("match")}
					>
						<span className="ecp-create-menu__dot ecp-create-menu__dot--match" />
						Nuova partita
					</button>
				</div>
			)}
		</Page>
	);
}

export default function EventsCalendarPage(props) {
	const user = useSelector((state) => state?.user),
		seasonId = useSelector((state) => state?.season?.id);
	const mode = props.mode || "all";
	const storagePrefix = `calendar_v3_${user?.clubId}_${user?.userId || user?.id}_${seasonId}_${mode}`;
	return (
		<ActivityCalendarView
			key={storagePrefix}
			{...props}
			mode={mode}
			storagePrefix={storagePrefix}
		/>
	);
}
